import type { UIMessage, UIMessagePart, UITools } from 'ai';
import z from 'zod';
export type AgentIds = 'agentFleetManager' | 'agentTrackingAnalyzer' | 'agentRecommendations';
export type AgentNames = 'agentFleetManager' | 'agent-tracking-analyzer' | 'agent-recommendations';

export type UserId = string;
export type FleetId = string;

export type Source = 'web' | 'mobile';
export type Component = 'full-page' | 'sidepanel' | 'floating' | 'sidepanel-tracking';

export type UserTextMessage = {
  role: 'user';
  id: string;
  parts: Array<{ type: 'text'; text: string }>;
  /** When `hidden` is true, the message drives an agent turn but is filtered from the rendered transcript. */
  metadata?: { hidden?: boolean };
};

export type SendUserTextMessage = (message: UserTextMessage) => Promise<void> | void;

export const AGENTS = {
  agentFleetManager: 'agentFleetManager',
  agentTrackingAnalyzer: 'agent-tracking-analyzer',
  agentRecommendations: 'agent-recommendations',
} as const satisfies Record<AgentIds, AgentNames>;

type DefaultResourceId = `${UserId}_${FleetId}`;
type AssistantResourceId = `assistant_${UserId}_${FleetId}`;
type TrackingResourceId = `tracking_${string}`;

export type ResourceId = DefaultResourceId | AssistantResourceId | TrackingResourceId;

export const toolNameMap: Record<string, string> = {
  // Domain: System/General Messaging
  'step-start': 'Iniciando...',
  text: 'Processando...',
  reasoning: 'Pensando...',

  'tool-invocation': 'Executando ferramenta',

  'tool-skill': 'Selecionando habilidade',
  'tool-skill-activate': 'Ativando habilidade',
  'tool-code_execution': 'Executando análise',
  'tool-execute-code': 'Executando código',

  // Domain: Files
  'tool-fileOperationsTool': 'Operações com arquivo(s)',
  'tool-executeCodeTool': 'Executando operação',
  'tool-executeCommandTool': 'Executando operação',
  'tool-uploadS3Tool': 'Salvando arquivo',

  'tool-load_tool': 'Carregando ferramenta...',
  'tool-search_tools': 'Buscando ferramentas...',

  // Domain: Maintenance
  'tool-maintenance-drilldown': 'Buscando detalhamento de manutenções',
  'tool-maintenance-reminders': 'Buscando dados sobre lembretes de manutenção',
  'tool-maintenance-history': 'Buscando dados sobre histórico de manutenções',
  'tool-maintenance_overview': 'Buscando dados sobre manutenções',

  // Domain: Charts
  'tool-chartsDataTool': 'Calculando dados para gráficos',
  'tool-performanceDataTool': 'Calculando comparações de performance',

  // Domain: Memory/Knowledge Base
  'tool-updateWorkingMemory': 'Atualizando memória de trabalho',
  'tool-knowledgeBaseTool': 'Buscando dados gerais',

  // Domain: Devices
  'tool-devicesOverviewTool': 'Buscando dados sobre dispositivos',

  // Domain: Drivers
  'tool-driversListTool': 'Buscando dados sobre motoristas',
  'tool-driverDetailsTool': 'Buscando detalhes do motorista',
  'tool-driversOverviewTool': 'Buscando dados sobre motoristas',
  'tool-driversLicenseExpirationTool': 'Buscando dados sobre CNH',
  'tool-driversAddTool': 'Adicionando motorista',

  // Domain: Vehicles
  'tool-vehicleTool': 'Buscando dados sobre veículo',
  'tool-vehiclesOverviewTool': 'Buscando dados sobre veículos',
  'tool-vehicleDetailsTool': 'Buscando detalhes do veículo',
  'tool-vehicleOverviewTool': 'Buscando dados sobre veículo',
  'tool-vehicleGroupsTool': 'Buscando dados sobre grupos de veículos',
  'tool-groupsTool': 'Buscando dados sobre grupos de veículos',
  'tool-vehiclesListTool': 'Construindo lista de veículos',

  // Domain: Security
  'tool-safety-overview': 'Buscando dados gerais sobre segurança',
  'tool-safety-ranking': 'Buscando dados sobre ranking de segurança',
  'tool-safety-max-speed-peak': 'Buscando dados sobre picos de velocidade',
  'tool-safety-entity-explanation': 'Buscando dados sobre explicação de entidades de segurança',
  'tool-safety-entities-query': 'Buscando dados sobre entidades de segurança',

  // Domain: Productivity
  'tool-productivityOverviewTool': 'Buscando informações gerais de produtividade',
  'tool-productivityEntitiesQueryTool': 'Buscando dados sobre entidades de produtividade',
  'tool-productivityEntityExplanationTool': 'Buscando dados sobre explicação de entidades de produtividade',

  // Domain: Video
  'tool-videoDailyRecommendationsTool': 'Buscando vídeos recomendados',

  // Domain: Maintenance
  'tool-maintenanceOverviewTool': 'Buscando dados sobre manutenções',
  'tool-maintenanceRemindersTool': 'Buscando dados sobre lembretes de manutenção',
  'tool-maintenanceHistoryTool': 'Buscando dados sobre histórico de manutenções',

  //Domain: Tracking
  'tool-trackingFetchDataTool': 'Buscando dados sobre trajeto',

  // Domain: Fuel
  'tool-fuelOverviewTool': 'Buscando dados sobre combustível',
  'tool-fuelTransactionsTool': 'Buscando dados sobre transações de combustível',
} as const;

/**
 * Represents tripwire data that can be included in AI messages.
 * Tripwires are warnings or safety checks that can interrupt or modify AI responses.
 */
const tripwireSchema = z.object({
  tripwireReason: z.string(),
});

export type TripwireData = z.infer<typeof tripwireSchema>;

const fileS3Schema = z.object({
  success: z.boolean(),
  status: z.enum(['loading', 'success', 'error']),
  data: z.array(
    z.object({
      toolCallId: z.string(),
      s3Path: z.string(),
      size: z.number(),
      filename: z.string(),
      s3Url: z.string(),
      contentType: z.string(),
      threadId: z.string().optional(),
    })
  ),
});

export type FileS3Data = z.infer<typeof fileS3Schema>;

const aiFileSchema = z.object({
  filePath: z.string(),
  filename: z.string(),
  threadId: z.string().optional(),
  threadTitle: z.string().optional(),
  url: z.string().optional(),
  size: z.number(),
  createdAt: z.string(),
  sessionId: z.string().optional(),
  userId: z.string().optional(),
  fleetId: z.string().optional(),
  contentType: z.string().optional(),
});

const aiFilesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(aiFileSchema),
});

export type AIFile = z.infer<typeof aiFileSchema>;
export type AIFilesResponse = z.infer<typeof aiFilesResponseSchema>;

/**
 * Metadata that can be attached to UI messages.
 */
const metadataSchema = z.object({
  threadId: z.string().optional(),
  traceId: z.string().optional(),
  resourceId: z.string().optional(),
  createdAt: z.string().datetime().optional(),
  inputTokens: z.number().optional(),
  outputTokens: z.number().optional(),
  reasoningTokens: z.number().optional(),
  cachedInputTokens: z.number().optional(),
  maxTokens: z.number().optional(),
  modelId: z.string().optional(),
  /**
   * Marks a message as hidden from the rendered transcript (e.g. prompts triggered by a
   * button click). Model-invisible — `convertToModelMessages` strips message metadata, so
   * the agent only sees the prompt text. Persisted with the message and restored on reload,
   * so the message stays hidden across sessions. Replaces the legacy `[[AI_HIDDEN_PROMPT]]`
   * text marker.
   */
  hidden: z.boolean().optional(),
});

type CobliChatMetadata = z.infer<typeof metadataSchema>;

/**
 * Schema for data parts in CobliChatUIMessage.
 * This schema is used to validate the data parts in the CobliChatUIMessage.
 */
const dataPartSchema = z.object({
  tripwire: tripwireSchema.optional(),
  file: fileS3Schema.optional(),
});

export type CobliChatDataPart = z.infer<typeof dataPartSchema>;

// https://ai-sdk.dev/docs/reference/ai-sdk-core/ui-message#creating-your-own-uimessage-type
export type CobliChatUIMessage = UIMessage<CobliChatMetadata, CobliChatDataPart, UITools>;

export type CobliChatUIMessagePart = UIMessagePart<CobliChatDataPart, UITools>;
